import { createSlice } from '@reduxjs/toolkit';
import { ICompetitorObject } from '../../Models/Responses/CompetitorResponse';
import { ISeedResponse } from '../../Models/Responses/SeedResponse';
import { CropAndBrandAvailability7, LocalPosition } from '../../Models/Responses/SeedResponseV7';
import { BrandLookup, HybridLookup, MaterialLookup, seedDataToLookups, SeriesLookup, TreatmentLookup } from './SeedLookups';
import { getSeedsForGrower, getEnogenContractCheck, getCompetitorData, getSeedsForUser, getSeedsForSeller, getLocalPositioning } from './SeedsActions';

export interface ISeedListState
{
	isError: boolean;
	isLoading: boolean;
	isLoadingContractCheck: boolean;
	isLoadingCompetitors: boolean;
	errorMessage: string;
	competitors: { [key: string]: ICompetitorObject[] };
	isEnogenContractValid: boolean;
	userSeeds: ISeedResponse[]; // These seeds are pulled from the userId and not the growerId
	allSeedsV7: CropAndBrandAvailability7[] | undefined;
	/** Flat lookups of the seed data for places in the UI that need lists of all of something. */
	brands: BrandLookup | undefined;
	series: SeriesLookup | undefined;
	hybrids: HybridLookup | undefined;
	treatments: TreatmentLookup | undefined;
	materials: MaterialLookup | undefined;
	localPositioning: { [key: string]: LocalPosition[]} | undefined;
}

export const initialState: ISeedListState = {
	isError: false,
	isLoading: false,
	errorMessage: undefined,
	competitors: {},
	isEnogenContractValid: false,
	isLoadingContractCheck: false,
	isLoadingCompetitors: false,
	userSeeds: undefined,
	allSeedsV7: undefined,
	brands: undefined,
	series: undefined,
	hybrids: undefined,
	treatments: undefined,
	materials: undefined,
	localPositioning: undefined,
};

export const seedsSlice = createSlice({
	name: 'seeds',
	initialState,
	reducers: {
		clearState: (state) => 
		{
			state.isError = false;
			state.isLoading = false;
			state.isLoadingContractCheck = false;
			state.isLoadingCompetitors = false;
			state.errorMessage = undefined;
			state.allSeedsV7 = undefined;
			state.brands = undefined;
			state.series = undefined;
			state.hybrids = undefined;
			state.treatments = undefined;
			state.materials = undefined;

			return state;
		},
		clearError: (state) =>
		{
			state.isError = false;
			state.errorMessage = undefined;
		},
		clearSeeds: (state) =>
		{
			state.allSeedsV7 = undefined;
			state.userSeeds = undefined;
			state.brands = undefined;
			state.series = undefined;
			state.hybrids = undefined;
			state.treatments = undefined;
			state.materials = undefined;

			return state;
		},
		clearOnlyUserSeeds: (state) =>
		{
			state.userSeeds = undefined;
			return state;
		}
	},
	extraReducers: (builder) =>
	{
		builder.addCase(getEnogenContractCheck.pending, (state, action) =>
		{
			state.isLoadingContractCheck = true;
		});
		builder.addCase(getEnogenContractCheck.fulfilled, (state, action) =>
		{
			state.isEnogenContractValid = action.payload;
			state.isLoadingContractCheck = false;
			state.isError = false;
			state.errorMessage = undefined;
		});
		builder.addCase(getEnogenContractCheck.rejected, (state, action) =>
		{
			state.isLoadingContractCheck = false;
			state.isError = true;

			if (action.payload)
			{
				if ((action.payload as string).indexOf('network') > -1)
				{
					state.errorMessage = 'There was a problem contacting the server to get the Enogen contract. Please refresh and try again.';
				}
				else
				{
					state.errorMessage = (action.payload as string);
				}
			}
			else
			{
				state.errorMessage = 'There was a problem getting the Enogen contract. Please refresh and try again.';
			}

		});
		builder.addCase(getSeedsForGrower.pending, (state, action) =>
		{
			state.isLoading = true;
		});
		builder.addCase(getSeedsForGrower.fulfilled, (state, action) =>
		{
			state.isLoading = false;
			state.errorMessage = undefined;
			state.isError = false;
			state.allSeedsV7 = action.payload.seeds;

			const lookups = seedDataToLookups(action.payload.seeds);
			state.brands = lookups.brands;
			state.series = lookups.series;
			state.hybrids = lookups.hybrids;
			state.treatments = lookups.treatments;
			state.materials = lookups.materials;
		});
		builder.addCase(getSeedsForGrower.rejected, (state, action) =>
		{
			state.isLoading = false;
			state.isError = true;
			state.allSeedsV7 = undefined;

			if (action.payload)
			{
				if ((action.payload as string).indexOf('network') > -1)
				{
					state.errorMessage = 'There was a problem contacting the server to get the list of Seeds. Please refresh and try again.';
				}
				else
				{
					state.errorMessage = (action.payload as string);
				}
			}
			else
			{
				state.errorMessage = 'There was a problem getting the list of Seeds. Please refresh and try again.';
			}

		});
		builder.addCase(getCompetitorData.pending, (state, action) =>
		{
			state.isLoadingCompetitors = true;
		});
		builder.addCase(getCompetitorData.fulfilled, (state, action) =>
		{
			state.competitors = action.payload;
			state.isError = false;
			state.errorMessage = undefined;
			state.isLoadingCompetitors = false;
		});
		builder.addCase(getCompetitorData.rejected, (state, action) =>
		{
			state.isError = true;
			state.competitors = {};
			state.isLoadingCompetitors = false;

			if (action.payload)
			{
				if ((action.payload as string).indexOf('network') > -1)
				{
					state.errorMessage = 'There was a problem contacting the server to get the list of Competitors. Please refresh and try again.';
				}
				else
				{
					state.errorMessage = (action.payload as string);
				}
			}
			else
			{
				state.errorMessage = 'There was a problem getting the list of Seeds. Please refresh and try again.';
			}
		});

		/**
		 * User Seeds -- For PPG pdf
		 */
		builder.addCase(getSeedsForUser.pending, (state, action) =>
		{
			state.isLoading = true;
		});
		builder.addCase(getSeedsForUser.fulfilled, (state, action) =>
		{
			state.userSeeds = action.payload;			
			state.isLoading = false;
			state.isError = false;
			state.errorMessage = undefined;
		});
		builder.addCase(getSeedsForUser.rejected, (state, action) =>
		{
			state.isLoading = false;
			state.isError = true;
			state.userSeeds = undefined;

			if (action.payload)
			{
				if ((action.payload as string).indexOf('network') > -1)
				{
					state.errorMessage = 'There was a problem contacting the server to get the list of Seeds for the User. Please refresh and try again.';
				}
				else
				{
					state.errorMessage = (action.payload as string);
				}
			}
			else
			{
				state.errorMessage = 'There was a problem getting the list of Seeds for the User. Please refresh and try again.';
			}

		});

		/**
		 * User/Seller Seeds -- For Field Confirmation
		 */
		builder.addCase(getSeedsForSeller.pending, (state, action) =>
		{
			state.isLoading = true;
		});
		builder.addCase(getSeedsForSeller.fulfilled, (state, action) =>
		{
			state.userSeeds = action.payload;			
			state.isLoading = false;
			state.isError = false;
			state.errorMessage = undefined;
		});
		builder.addCase(getSeedsForSeller.rejected, (state, action) =>
		{
			state.isLoading = false;
			state.isError = true;
			state.userSeeds = undefined;

			if (action.payload)
			{
				if ((action.payload as string).indexOf('network') > -1)
				{
					state.errorMessage = 'There was a problem contacting the server to get the list of Seeds for the Seller. Please refresh and try again.';
				}
				else
				{
					state.errorMessage = (action.payload as string);
				}
			}
			else
			{
				state.errorMessage = 'There was a problem getting the list of Seeds for the Seller. Please refresh and try again.';
			}
		});


		/**
		 * Local positioning
		 */
		builder.addCase(getLocalPositioning.pending, (state, action) =>
		{
			state.isLoading = true;
		});
		builder.addCase(getLocalPositioning.fulfilled, (state, action) =>
		{
			state.localPositioning = action.payload;			
			state.isLoading = false;
			state.isError = false;
			state.errorMessage = undefined;
		});
		builder.addCase(getLocalPositioning.rejected, (state, action) =>
		{
			state.isLoading = false;
			state.isError = true;
			state.userSeeds = undefined;

			if (action.payload)
			{
				if ((action.payload as string).indexOf('network') > -1)
				{
					state.errorMessage = 'There was a problem contacting the server to get the local positioning for the Grower. Please refresh and try again.';
				}
				else
				{
					state.errorMessage = (action.payload as string);
				}
			}
			else
			{
				state.errorMessage = 'There was a problem getting the local positioning for the Grower. Please refresh and try again.';
			}

		});
	},
});

export const { clearState, clearError, clearSeeds, clearOnlyUserSeeds } = seedsSlice.actions;